import { useEffect, useState } from 'react'
import { Plus, Trash2, UserCircle } from 'lucide-react'
import { apiGet } from '../../services/api'
import {
  useProjectStore,
  type Project,
  type ProjectMember,
  type ProjectMemberRole,
} from '../../store/projectStore'

interface UserOption {
  id: number
  name: string
  email: string
}

interface ProjectMemberPickerProps {
  project: Project
  canEdit: boolean
}

const ROLE_LABEL: Record<ProjectMemberRole, string> = {
  manager: 'Manager',
  member: 'Member',
  viewer: 'Viewer',
}

export default function ProjectMemberPicker({ project, canEdit }: ProjectMemberPickerProps) {
  const { addMember, removeMember } = useProjectStore()
  const [users, setUsers] = useState<UserOption[]>([])
  const [newUserId, setNewUserId] = useState<number | ''>('')
  const [newRole, setNewRole] = useState<ProjectMemberRole>('member')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    apiGet<{ items: UserOption[] }>('/projects/users')
      .then((res) => setUsers(res.items || []))
      .catch(() => {})
  }, [])

  const memberIds = new Set((project.members || []).map((m) => m.user_id))
  const availableUsers = users.filter((u) => !memberIds.has(u.id))

  const handleAdd = async () => {
    if (newUserId === '') return
    setBusy(true)
    try {
      await addMember(project.id, Number(newUserId), newRole)
      setNewUserId('')
      setNewRole('member')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (member: ProjectMember) => {
    if (!confirm(`Remove ${member.user_name || 'this user'} from the project?`)) return
    await removeMember(project.id, member.user_id)
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-900">Members</h3>

      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
        {(project.members || []).map((m) => (
          <li key={m.id} className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <UserCircle className="h-7 w-7 text-gray-400 shrink-0" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{m.user_name || `User #${m.user_id}`}</p>
                <p className="truncate text-xs text-gray-500">{m.user_email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                {ROLE_LABEL[m.role_on_project]}
              </span>
              {canEdit && m.user_id !== project.owner_id && (
                <button
                  onClick={() => handleRemove(m)}
                  className="text-red-600 hover:text-red-700"
                  aria-label="Remove member"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </li>
        ))}
        {(project.members || []).length === 0 && (
          <li className="px-3 py-4 text-center text-sm text-gray-500">No members yet</li>
        )}
      </ul>

      {canEdit && (
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value === '' ? '' : Number(e.target.value))}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Add user…</option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as ProjectMemberRole)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="manager">Manager</option>
            <option value="member">Member</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            onClick={handleAdd}
            disabled={busy || newUserId === ''}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      )}
    </div>
  )
}
